import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  PriorityHigh as PriorityHighIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  AddAlert as AddAlertIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import { ReminderFormData } from '../../types';


const CreateEditReminder: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<ReminderFormData>({
    defaultValues: {
      date: new Date(),
      reminderDate: new Date(),
      nextReminderDate: null,
      priority: 'MEDIUM',
      description: '',
      status: 'PENDING',
      statusNote: '',
    }
  });

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch reminder data if in edit mode
  const fetchReminderData = useCallback(async () => {
    if (!isEditMode || !id) return;

    setLoading(true);
    try {
      const response = await request({
        url: `reminder/get-single-reminder/${id}`,
        method: 'get',
      });

      const reminderData = response.data || response;

      if (!reminderData) {
        throw new Error('Reminder data not found');
      }

      // Format data for form
      const formattedData: ReminderFormData = {
        date: reminderData.date ? new Date(reminderData.date) : new Date(),
        reminderDate: reminderData.reminderDate ? new Date(reminderData.reminderDate) : new Date(),
        nextReminderDate: reminderData.nextReminderDate ? new Date(reminderData.nextReminderDate) : null,
        priority: reminderData.priority || 'LOW',
        description: reminderData.description || '',
        status: reminderData.status || 'PENDING',
        statusNote: reminderData.statusNote || '',
      };

      reset(formattedData);
    } catch (err: any) {
      console.error('Error fetching reminder:', err);
      toast.error(err.message || 'Failed to load reminder data');
      navigate('/reminder');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset, navigate]);

  // Fetch data on component mount
  useEffect(() => {
    if (isEditMode) {
      fetchReminderData();
    } else {
      setLoading(false);
    }
  }, [isEditMode, fetchReminderData]);

  // Handle form submission
  const onSubmit = async (data: ReminderFormData) => {
    setSaving(true);

    try {
      const payload: any = {
        date: data.date ? format(new Date(data.date), 'yyyy-MM-dd') : null,
        reminderDate: data.reminderDate ? format(new Date(data.reminderDate), 'yyyy-MM-dd HH:mm:ss') : null,
        nextReminderDate: data.nextReminderDate ? format(new Date(data.nextReminderDate), 'yyyy-MM-dd HH:mm:ss') : null,
        priority: data.priority,
        description: data.description,
        status: data.status,
        statusNote: data.statusNote || '',
      };

      // Remove null values
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === '') {
          delete payload[key];
        }
      });

      const apiUrl = isEditMode ? `reminder/update-reminder/${id}` : '/reminder/create-reminder';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload,
      });

      if (res.success) {
        toast.success(res.message || `Reminder ${isEditMode ? 'updated' : 'created'} successfully!`);
        navigate('/reminder');
      } else {
        toast.error(res.message || 'Operation failed');
      }
    } catch (err: any) {
      console.error('Error saving reminder:', err);
      toast.error(err.message || 'Failed to save reminder');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
          Loading reminder data...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            p: 2,
            backgroundColor: 'white',
            borderRadius: 1,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {isEditMode ? 'Edit Reminder' : 'Create New Reminder'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.9 }}>
                  {isEditMode ? 'Update reminder details' : 'Set up a new reminder for important tasks'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Card elevation={2} sx={{ borderRadius: 1 }}>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>

                  {/* LEFT COLUMN - Reminder Details */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main'
                    }}>
                      <AddAlertIcon />
                      Reminder Details
                    </Typography>

                    {/* Event Date and Status in one row */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="date"
                          control={control}
                          rules={{ required: 'Event date is required' }}
                          render={({ field }) => (
                            <DatePicker
                              label="Event Date *"
                              value={field.value}
                              onChange={field.onChange}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  error: !!errors.date,
                                  helperText: errors.date?.message,
                                }
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth size="small">
                              <InputLabel>Status</InputLabel>
                              <Select
                                {...field}
                                label="Status"
                              >
                                <MenuItem value="PENDING">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>⏳</span>
                                    Pending
                                  </Box>
                                </MenuItem>
                                <MenuItem value="CLOSED">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>✅</span>
                                    Closed
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                    </Box>

                    {/* Reminder Date and Time */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="reminderDate"
                          control={control}
                          rules={{ required: 'Reminder date is required' }}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Reminder Date & Time *"
                              value={field.value}
                              onChange={field.onChange}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  error: !!errors.reminderDate,
                                  helperText: errors.reminderDate?.message,
                                }
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="nextReminderDate"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Next Reminder (Optional)"
                              value={field.value}
                              onChange={field.onChange}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                }
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Box>

                    {/* Priority */}
                    <Box sx={{ mb: 2 }}>
                      <Controller
                        name="priority"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select
                              {...field}
                              label="Priority"
                            >
                              <MenuItem value="LOW">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PriorityHighIcon color="success" />
                                  Low Priority
                                </Box>
                              </MenuItem>
                              <MenuItem value="MEDIUM">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PriorityHighIcon color="warning" />
                                  Medium Priority
                                </Box>
                              </MenuItem>
                              <MenuItem value="HIGH">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PriorityHighIcon color="error" />
                                  High Priority
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>

                    {/* Description */}
                    <Controller
                      name="description"
                      control={control}
                      rules={{ required: 'Description is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Description *"
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          multiline
                          rows={4}
                          size="small"
                          sx={{ mb: 2 }}
                          placeholder="Enter reminder details..."
                        />
                      )}
                    />

                    {/* Status Note */}
                    <Controller
                      name="statusNote"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Status Note (Optional)"
                          placeholder="Add a note about this reminder..."
                          multiline
                          rows={2}
                          size="small"
                          sx={{ mb: 2 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <DescriptionIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* RIGHT COLUMN - Additional Information */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main'
                    }}>
                      <ScheduleIcon />
                      Summary
                    </Typography>

                    {/* Information Cards */}
                    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                        Reminder Summary
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Event Date:</strong> {watch('date') ? format(new Date(watch('date') as any), 'dd/MM/yyyy') : 'Not set'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Reminder Date:</strong> {watch('reminderDate') ? format(new Date(watch('reminderDate') as any), 'dd/MM/yyyy HH:mm') : 'Not set'}
                          </Typography>
                        </Box>
                        {watch('nextReminderDate') && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AddAlertIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Next Reminder:</strong> {format(new Date(watch('nextReminderDate') as any), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PriorityHighIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Priority:</strong> {watch('priority')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>📋</span>
                          <Typography variant="body2">
                            <strong>Status:</strong> {watch('status')}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Priority Tips */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                        Priority Guidelines
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PriorityHighIcon color="error" fontSize="small" />
                          <Typography variant="body2">
                            <strong>High Priority:</strong> Urgent tasks that need immediate attention
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PriorityHighIcon color="warning" fontSize="small" />
                          <Typography variant="body2">
                            <strong>Medium Priority:</strong> Important but not urgent tasks
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PriorityHighIcon color="success" fontSize="small" />
                          <Typography variant="body2">
                            <strong>Low Priority:</strong> Tasks that can be done later
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Info Alert */}
                    {watch('status') === 'CLOSED' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          This reminder is marked as closed. You can reopen it by changing the status to Pending.
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Action Buttons */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}>
                  <Button
                    variant="outlined"
                    size='small'
                    color="inherit"
                    onClick={() => navigate(-1)}
                    disabled={saving}
                    sx={{ minWidth: 120, px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size='small'
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={saving}
                    sx={{ minWidth: 140, px: 4 }}
                  >
                    {saving ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      isEditMode ? 'Update Reminder' : 'Create Reminder'
                    )}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>
      </LocalizationProvider>
    </>
  );
};

export default CreateEditReminder;